import WineHero from 'assets/images/psp/PSPHero/wine-hero.webp';

import FaQHeadingIcon from 'assets/images/psp/Faq/StorageImage.svg';

import PersonalWineLockersIcon from 'assets/images/psp/SizeGuide/personal-wine-lockers.svg';
import StandardWineUnitsIcon from 'assets/images/psp/SizeGuide/standard-wine-units.svg';
import PremiumWineStorageIcon from 'assets/images/psp/SizeGuide/premium-wine-storage.svg';
import BulkWineStorageIcon from 'assets/images/psp/SizeGuide/bulk-wine-storage.svg';
import CollectorVaultsIcon from 'assets/images/psp/SizeGuide/collector-vaults.svg';
import RestaurantCaterersIcon from 'assets/images/psp/SizeGuide/restaurant-caterers.svg';

import TemperatureStabilityIcon from 'assets/images/psp/SizeGuide/temperature-stability.svg';
import HumidityControlIcon from 'assets/images/psp/SizeGuide/humidity-control.svg';
import AirCirculationIcon from 'assets/images/psp/SizeGuide/air-circulation.svg';
import VibrationEliminationIcon from 'assets/images/psp/SizeGuide/vibration-elimination.svg';
import LightProtectionIcon from 'assets/images/psp/SizeGuide/light-protection.svg';

import IndoorClimateStorageUnit from 'assets/images/psp/FeaturedContentImages/indoor-climate-controlled-storage-units.webp';
import Fresno from 'assets/images/psp/FeaturedContentImages/fresno.jpg';
import SacramentoCa from 'assets/images/psp/FeaturedContentImages/sacramento-ca.webp';

import { storageCategories } from 'pages/PSPPage/utils/getPSPLinks';

const reviews = [
	{
		title: 'Easy reservation and confirmation',
		stars: 5,
		text: 'Easy reservation and confirmation with total access and payment for the storage unit online. The unit is clean and climate controlled, and a really beautiful facility!',
		customerName: 'Edward',
		date: 'Jun 12, 2024',
		link: 'https://www.trustpilot.com/reviews/6668e8d3655b5a32107bff66',
	},
	{
		title: 'Making reservations was so easy!',
		stars: 5,
		text: 'I needed a small climate controlled storage space in a hurry, and SelfStorage had the best price.',
		customerName: 'Kem Washington',
		date: 'May 15, 2024',
		link: 'https://www.trustpilot.com/reviews/6643ccb7603d6d580747ba61',
	},
	{
		title: 'Simple and easy!',
		stars: 5,
		text: 'I was able to look at the sizes and compare prices on SelfStorage.com. I was able to compare climate and non-climate controlled units. I liked that it was self-explanatory and simple.',
		customerName: 'Brandy Wilson',
		date: 'May 5, 2024',
		link: 'https://www.trustpilot.com/reviews/66366758ea02080604ca2773',
	},
	{
		title: 'Reserving a storage space was quick',
		stars: 5,
		text: "Reserving a storage space was quick, easy, and I didn't have to pay anything at the time of reserving, but instead paid once I arrived to check the unit's access. Low to moderate prices, and they have a variety of storage spaces from climate controlled to drive-up access. I would recommend them to my peeps.",
		customerName: 'Cherie Hancock',
		date: 'Nov 3, 2023',
		link: 'https://www.trustpilot.com/reviews/654515a29e19bf686949018a',
	},
];

const faqs = [
	{
		question: 'What size wine storage unit do I need for my collection?',
		answer: 'Most wine collectors start with units accommodating 50-100 bottles, which typically measure around 25-50 square feet. A standard wine bottle takes up roughly 0.1 square feet of storage space when properly racked.\n\n Bulk wine storage becomes necessary for collections exceeding 500 bottles, requiring larger units with multiple temperature zones for different wine types.',
	},
	{
		question:
			'How do professional wine storage facilities maintain proper humidity and temperature?',
		answer: 'The best wine storage facilities use commercial-grade HVAC systems specifically designed for wine preservation. These systems monitor temperature and humidity continuously, making micro-adjustments to maintain optimal conditions.',
	},
	{
		question:
			'Is professional wine storage worth the cost compared to home solutions?',
		answer: 'Wine storage cost analysis depends on your collection size and timeline. Professional storage typically runs $0.50-$2.00 per bottle monthly, while building a home cellar costs $15-50 per bottle capacity upfront.',
	},
	{
		question:
			'Can I store different types of wine together in the same unit?',
		answer: 'Most wine storage solutions accommodate mixed collections in single units, as optimal storage temperatures (55-58°F) work for reds, whites, and sparkling wines alike.',
	},
	{
		question:
			'What security measures should I expect from wine storage facilities?',
		answer: 'Professional wine storage facilities typically provide multi-layered security, including gated access, individual unit locks, surveillance cameras, and on-site management. Many offer wine lockers with additional security features like individual alarms and restricted access logs.',
	},
];

export const WineContent = {
	type: 'Wine',
	reviews: reviews,
	sections: [
		{
			title: 'Wine Storage Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'wine-storage-hero',
					props: {
						content: {
							title: 'Find Affordable Wine Self Storage Options',
							description:
								'Building a proper wine collection means more than just buying great bottles – it requires the right storage environment to protect your investment.\n\n Wine storage units with [precise climate control](https://www.selfstorage.com/blog/what-is-climate-controlled-storage-2/) ensure your collection develops properly, maintains its value, and tastes exactly as the winemaker intended.\n\n **SelfStorage.com** makes it easy to find temperature controlled storage for bottles, with facilities that specialize in wine preservation and collection management.',
							image: WineHero,
							CTAtext: 'Find Wine Self Storage',
						},
					},
				},
			],
		},
		{
			title: 'content section 1',
			background: 'lighterBlue',
			modules: [
				{
					title: 'content section 1',
					type: 'content',
					props: {
						title: 'Wine Storage Cost vs. Home Cellar Reality',
						contentType: 'row-type',
						theme: 'light',
						content: [
							'Most wine enthusiasts face the same dilemma: You want to age your wine properly, but home storage options are limited and expensive.\n\n' +
								'Kitchen wine racks expose bottles to temperature swings, basements are too humid or dry, and building a custom cellar comes with astronomical overhead costs before you can store a single bottle.\n\n' +
								'Professional wine storage units solve this challenge affordably. With monthly rates starting around $30-50 for small collections, you get restaurant-quality storage conditions without the massive upfront investment. Wine storage cost becomes predictable and scalable as your collection grows.\n\n' +
								'The key is finding facilities that understand wine storage humidity requirements (55-65%), maintain consistent red wine temperature storage conditions (55-58°F), and provide the security your collection deserves.\n\n',
						],
					},
				},
			],
		},

		{
			title: 'unit size chart 1',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading: 'Wine Storage Solutions by Collection Type',
						description:
							'Your wine storage requirements will vary greatly depending on the size of your collection. Here’s how to figure out what size wine storage unit you need to house your collection today, tomorrow, and beyond.',
						content:
							'*Regional pricing variations apply. Additional fees may include setup, insurance, and premium access options.',
						cardData: [
							{
								title: 'Personal Wine Lockers',
								content:
									'**Personal Wine Lockers**\n' +
									'- **Average Monthly Cost:** $30-75\n' +
									'- **Bottle Capacity:** 25-100 bottles\n' +
									'- **Features:** Individual access, basic climate control\n',
								imageSrc: PersonalWineLockersIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Standard Wine Units',
								content:
									'**Standard Wine Units**\n' +
									'- **Average Monthly Cost:** $80-150\n' +
									'- **Bottle Capacity:** - 100-300 bottles\n' +
									'- **Features:** Professional temperature control, humidity management\n',
								imageSrc: StandardWineUnitsIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Premium Wine Storage',
								content:
									'**Premium Wine Storage**\n' +
									'- **Average Monthly Cost:** $175-350\n' +
									'- **Bottle Capacity:** - 300-750 bottles\n' +
									'- **Features:** Dual-zone temperature, advanced security, and inventory tracking\n',
								imageSrc: PremiumWineStorageIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Bulk Wine Storage',
								content:
									'**Bulk Wine Storage**\n' +
									'- **Average Monthly Cost:** $400-800\n' +
									'- **Bottle Capacity:** - 1,000+ bottles\n' +
									'- **Features:** Commercial-grade systems, multiple temperature zones\n',
								imageSrc: BulkWineStorageIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Collector Vaults',
								content:
									'**Collector Vaults**\n' +
									'- **Average Monthly Cost:** $500-1,200\n' +
									'- **Bottle Capacity:** - 1,500+ bottles\n' +
									'- **Features:** Museum-quality conditions, white-glove services\n',
								imageSrc: CollectorVaultsIcon,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'content section 2',
			background: 'blueWithMap',
			modules: [
				{
					title: 'content section 2',
					type: 'content',
					props: {
						title: 'How to Store Wine Without a Cellar: Professional Alternatives',
						contentType: 'row-type',
						theme: 'dark',
						content: [
							'You love a good [DIY home renovation](https://www.selfstorage.com/blog/10-diy-home-projects-under-100-dollars/) as much as the next person, but you don’t have the resources, time, or desire to convert your unfinished basement into a wine cellar oasis.\n\n' +
								'And you’re not alone — that’s why we’re here to help you and other sensible collectors figure out the most affordable, safe ways to store wine of all kinds.\n\n' +
								"The best wine storage doesn't require owning a mansion with a basement. Modern wine storage facilities use advanced HVAC systems to create ideal aging environments that surpass most home cellars.\n\n" +
								'These facilities maintain precise temperature controlled storage for bottles, eliminate vibrations that can disturb sediment, and protect against light exposure that degrades wine quality.\n\n' +
								'**Professional wine storage advantages:** \n\n' +
								'- Consistent 55-58°F temperatures year-round (no seasonal fluctuations)\n\n' +
								'- Controlled 55-65% humidity levels for cork preservation\n\n' +
								'- Vibration-free environments for proper aging\n\n' +
								'- UV-protected storage areas\n\n' +
								'- Professional security and insurance options',
						],
					},
				},
			],
		},

		{
			title: 'unit size chart 2',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'Wine Collections That Need Professional Storage',
						description:
							'Different types of wine enthusiasts benefit from specialized storage solutions:',
						cardData: [
							{
								title: 'New Collectors',
								content:
									'**New Collectors**\n' +
									'- 25-100 bottles\n' +
									'- Benefit from affordable wine storage solutions\n' +
									'- Flexibility to grow the collection\n',
								imageSrc: PremiumWineStorageIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Serious Enthusiasts',
								content:
									'**Serious Enthusiasts**\n' +
									'- 100-500 bottles\n' +
									'- Require precise climate control\n' +
									'- Benefits from organizational systems\n',
								imageSrc: PersonalWineLockersIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Wine Investors',
								content:
									'**Wine Investors**\n' +
									'- High-value bottles\n' +
									'- Require protection and optimal aging conditions\n',
								imageSrc: BulkWineStorageIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Restaurant Owners/Caterers',
								content:
									'**Restaurant Owners/Caterers**\n' +
									'- Bulk wine storage\n' +
									'- Inventory management\n' +
									'- Cost control\n',
								imageSrc: RestaurantCaterersIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Event Planners',
								content:
									'**Event Planners**\n' +
									'- Seasonal wine storage\n' +
									'- Corporate gifts\n' +
									'- Special occasion bottle storage\n',
								imageSrc: CollectorVaultsIcon,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'content section 3',
			background: 'blueWithMap',
			modules: [
				{
					title: 'content section 3',
					type: 'content',
					props: {
						title: 'Temperature and Humidity: The Science of Wine Preservation',
						contentType: 'row-type',
						theme: 'dark',
						content: [
							'Wine storage humidity and temperature work together to preserve your collection. Too dry (below 50% humidity) and corks shrink, allowing air to spoil the wine. Too humid (above 70%) and labels deteriorate while mold becomes a risk.\n\n' +
								'The sweet spot of 55-65% humidity keeps corks properly sealed while protecting your investment.\n\n' +
								'Red wine temperature storage follows the same principles as white wine – despite serving differences, both should be stored at 55-58°F. This temperature range slows aging to the proper pace while preventing the chemical reactions that occur at higher temperatures.',
						],
					},
				},
			],
		},

		{
			title: 'content section 3',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'Key Storage Factors Professional Facilities Control:',
						cardData: [
							{
								title: 'Temperature Stability',
								content: '±2°F variance maximum',
								imageSrc: TemperatureStabilityIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Humidity Control',
								content: 'Automated systems maintain 55-65%',
								imageSrc: HumidityControlIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Air Circulation',
								content:
									'Prevents stagnant air and odor absorption',
								imageSrc: AirCirculationIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Vibration Elimination',
								content:
									'Specialized flooring and equipment placement',
								imageSrc: VibrationEliminationIcon,
								contentAlign: 'text-left',
							},
							{
								title: 'Light Protection',
								content:
									'UV-filtered or eliminated lighting systems',
								imageSrc: LightProtectionIcon,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			modules: [
				{
					type: 'reviews',
					props: {
						reviewsData: {
							slug: 'wine-reviews',
							headerTitle: 'Wine Self Storage: ',
							reviews: reviews,
						},
					},
				},
			],
		},

		{
			title: 'Faqs Section',
			noPaddingTop: true,
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Wine Self Storage FAQs',
							image: FaQHeadingIcon,
							faqs,
						},
					},
				},
			],
		},

		{
			title: 'featured content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About Wine Self Storage',
							slug: 'wine-featured-content',
							data: {
								unitCards: [
									{
										title: 'Storage Tips',
										url: 'https://www.selfstorage.com/blog/what-is-climate-controlled-storage-2/',
										date: 'August 19, 2025',
										image: IndoorClimateStorageUnit,
										content:
											'Why Would You Need a Climate-Controlled Storage Unit?',
									},
									{
										title: 'City Guide',
										url: 'https://www.selfstorage.com/blog/things-to-know-before-moving-to-sacramento/',
										date: 'October 5, 2023',
										image: SacramentoCa,
										content: 'Moving to Sacramento',
									},
									{
										title: 'City Guide',
										url: 'https://www.selfstorage.com/blog/things-you-need-to-know-before-moving-to-fresno/',
										date: 'July 23, 2025',
										image: Fresno,
										content: 'Moving to Fresno',
									},
								],
							},
						},
					},
				},
			],
		},
		{
			title: 'Link to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More ways to store',
						},
						type: 'unitCards',
						id: 'storage size unit cards',
						linksList: storageCategories['wineStorage'],
					},
				},
			],
		},
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Wine',
					},
				},
			],
		},
	],
};
